import {
  PublicClientApplication,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';

interface RuntimeConfig {
  tenantId: string;
  clientId: string;
  scope: string;
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let client: PublicClientApplication | null = null;
let config: RuntimeConfig | null = null;

/**
 * Called once before rendering. Kept explicit rather than a top-level
 * await so a failure here can show a real message instead of a blank
 * page, which is what an unhandled module-level rejection produces.
 */
export async function initAuth(): Promise<{ signedIn: boolean }> {
  const response = await fetch('/config');
  if (!response.ok) throw new Error('Could not load sign-in settings.');
  config = (await response.json()) as RuntimeConfig;

  client = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // Survives a refresh, which matters on mobile where the browser
      // discards the tab aggressively.
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  });

  await client.initialize();
  const redirect = await client.handleRedirectPromise();

  if (redirect?.account) {
    client.setActiveAccount(redirect.account);
  } else if (!client.getActiveAccount()) {
    const [first] = client.getAllAccounts();
    if (first) client.setActiveAccount(first);
  }

  return { signedIn: client.getActiveAccount() !== null };
}

function ready(): { pca: PublicClientApplication; settings: RuntimeConfig } {
  if (!client || !config) throw new Error('Auth has not been initialised.');
  return { pca: client, settings: config };
}

async function accessToken(): Promise<string> {
  const { pca, settings } = ready();
  const account = pca.getActiveAccount();
  if (!account) throw new ApiError('not_signed_in', 'Sign in to continue.', 401);

  try {
    const result = await pca.acquireTokenSilent({ scopes: [settings.scope], account });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Redirect rather than popup: popups are blocked by default in
      // iOS Safari, which is where a lot of these bookings happen.
      await pca.acquireTokenRedirect({ scopes: [settings.scope], account });
    }
    throw error;
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await accessToken();

  // Only declare a JSON body when there is one. Sending the header on a
  // bodyless DELETE makes the server's JSON parser reject the request.
  const headers: Record<string, string> = {
    authorization: `Bearer ${token}`,
    ...(init.headers as Record<string, string> | undefined),
  };
  if (init.body !== undefined && init.body !== null) {
    headers['content-type'] = 'application/json';
  }

  const response = await fetch(`/api${path}`, { ...init, headers });

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (body as { error?: { code: string; message: string } } | null)?.error;
    throw new ApiError(
      error?.code ?? 'request_failed',
      error?.message ?? 'Something went wrong. Try again.',
      response.status,
    );
  }
  return body as T;
}

export function signIn(): Promise<void> {
  const { pca, settings } = ready();
  return pca.loginRedirect({ scopes: [settings.scope] });
}

export function signOut(): Promise<void> {
  return ready().pca.logoutRedirect();
}
